(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class BalaoCarousel {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-balao-viewport]');
      this.track = root.querySelector('[data-balao-track]');
      this.slides = [...root.querySelectorAll('[data-balao-slide]')];
      this.prevBtn = root.querySelector('[data-balao-prev]');
      this.nextBtn = root.querySelector('[data-balao-next]');
      this.dotsWrap = root.querySelector('[data-balao-dots]');
      this.index = 0;
      this.perViewDesktop = Number(root.dataset.perView || 3);
      this.gap = 0;
      this.slideWidth = 0;
      this.loop = root.dataset.loop === 'true';
      this.effect = root.dataset.effect || 'slide';
      this.autoplayMs = Number(root.dataset.autoplay || 0) * 1000;
      this.timer = null;
      this.isFade = this.effect === 'fade';

      if (!this.viewport || !this.track || this.slides.length === 0) return;

      if (this.isFade) {
        this.root.classList.add('balao-carousel--fade');
        this.track.style.transform = 'none';
        this.track.style.transition = 'none';
      }

      this.#bind();
      this.#layout();
      this.#renderDots();
      this.#goTo(0, false);
      this.#startAutoplay();

      this.ro = new ResizeObserver(() => {
        this.#layout();
        if (!this.isFade) this.#goTo(this.index, false);
      });
      this.ro.observe(this.viewport);
    }

    get perView() {
      if (this.isFade) return 1;
      const width = window.innerWidth;
      if (width < 768) return 1;
      if (width < 1024) return Math.min(2, this.perViewDesktop);
      return Math.min(this.perViewDesktop, this.slides.length);
    }

    get maxIndex() {
      return Math.max(0, this.slides.length - this.perView);
    }

    #wrap(index) {
      if (!this.loop || this.maxIndex === 0) {
        return Math.min(Math.max(index, 0), this.maxIndex);
      }
      if (index < 0) return this.maxIndex;
      if (index > this.maxIndex) return 0;
      return index;
    }

    #startAutoplay() {
      this.#stopAutoplay();
      if (reduceMotion || this.autoplayMs <= 0 || this.slides.length < 2) return;
      this.timer = window.setInterval(() => this.#goTo(this.index + 1), this.autoplayMs);
    }

    #stopAutoplay() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    #restartAutoplay() {
      this.#stopAutoplay();
      this.#startAutoplay();
    }

    #bind() {
      this.prevBtn?.addEventListener('click', () => {
        this.#goTo(this.index - 1);
        this.#restartAutoplay();
      });
      this.nextBtn?.addEventListener('click', () => {
        this.#goTo(this.index + 1);
        this.#restartAutoplay();
      });

      this.root.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.#goTo(this.index - 1);
          this.#restartAutoplay();
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.#goTo(this.index + 1);
          this.#restartAutoplay();
        }
        if (event.key === 'Home') {
          event.preventDefault();
          this.#goTo(0);
          this.#restartAutoplay();
        }
        if (event.key === 'End') {
          event.preventDefault();
          this.#goTo(this.maxIndex);
          this.#restartAutoplay();
        }
      });

      this.root.addEventListener('mouseenter', () => this.#stopAutoplay());
      this.root.addEventListener('mouseleave', () => this.#startAutoplay());
      this.root.addEventListener('focusin', () => this.#stopAutoplay());
      this.root.addEventListener('focusout', () => this.#startAutoplay());

      if (this.isFade) {
        let startX = 0;
        let deltaX = 0;
        let dragging = false;

        this.viewport.addEventListener(
          'touchstart',
          (event) => {
            dragging = true;
            startX = event.touches[0].clientX;
            deltaX = 0;
            this.#stopAutoplay();
          },
          { passive: true }
        );
        this.viewport.addEventListener(
          'touchmove',
          (event) => {
            if (!dragging) return;
            deltaX = event.touches[0].clientX - startX;
          },
          { passive: true }
        );
        this.viewport.addEventListener('touchend', () => {
          if (!dragging) return;
          dragging = false;
          const threshold = Math.max(40, this.viewport.clientWidth * 0.15);
          if (deltaX <= -threshold) this.#goTo(this.index + 1);
          else if (deltaX >= threshold) this.#goTo(this.index - 1);
          this.#restartAutoplay();
        });
        return;
      }

      let startX = 0;
      let deltaX = 0;
      let dragging = false;

      const onStart = (clientX) => {
        dragging = true;
        startX = clientX;
        deltaX = 0;
        this.#stopAutoplay();
        this.track.style.transition = 'none';
      };

      const onMove = (clientX) => {
        if (!dragging) return;
        deltaX = clientX - startX;
        const step = (this.slideWidth || this.viewport.clientWidth) + this.gap;
        const offset = this.index * step;
        this.track.style.transform = `translate3d(${-offset + deltaX}px, 0, 0)`;
      };

      const onEnd = () => {
        if (!dragging) return;
        dragging = false;
        this.track.style.transition = '';
        const threshold = Math.max(40, this.viewport.clientWidth * 0.15);
        if (deltaX <= -threshold) this.#goTo(this.index + 1);
        else if (deltaX >= threshold) this.#goTo(this.index - 1);
        else this.#goTo(this.index);
        this.#restartAutoplay();
      };

      this.viewport.addEventListener(
        'touchstart',
        (event) => onStart(event.touches[0].clientX),
        { passive: true }
      );
      this.viewport.addEventListener(
        'touchmove',
        (event) => onMove(event.touches[0].clientX),
        { passive: true }
      );
      this.viewport.addEventListener('touchend', onEnd);
      this.viewport.addEventListener('touchcancel', onEnd);
    }

    #layout() {
      if (this.isFade) {
        this.slides.forEach((slide) => {
          slide.style.flex = '';
          slide.style.width = '';
          slide.style.maxWidth = '';
          slide.style.minWidth = '';
          slide.style.marginRight = '';
        });
        return;
      }

      const rawGap = getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap || '0';
      const parsedGap = Number.parseFloat(rawGap);
      this.gap = Number.isFinite(parsedGap) ? parsedGap : 0;

      const viewportWidth = this.viewport.clientWidth;
      const slideWidth =
        this.perView <= 1
          ? viewportWidth
          : (viewportWidth - this.gap * (this.perView - 1)) / this.perView;

      this.slideWidth = slideWidth;
      this.slides.forEach((slide) => {
        slide.style.flex = `0 0 ${slideWidth}px`;
        slide.style.width = `${slideWidth}px`;
        slide.style.maxWidth = `${slideWidth}px`;
        slide.style.minWidth = `${slideWidth}px`;
        slide.style.marginRight = '0';
      });
    }

    goTo(index, animate = true) {
      this.#goTo(index, animate);
    }

    #goTo(index, animate = true) {
      const next = this.#wrap(index);
      const prev = this.index;
      this.index = next;

      if (this.isFade) {
        this.slides.forEach((slide, i) => {
          const isActive = i === next;
          slide.classList.toggle('is-active', isActive);
          slide.classList.toggle('is-prev', !animate && i !== next ? false : i === prev && prev !== next);
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

          if (!animate || reduceMotion) {
            slide.classList.add('is-instant');
            requestAnimationFrame(() => slide.classList.remove('is-instant'));
          }
        });
        this.#syncControls();
        this.#announce();
        return;
      }

      if (!animate || reduceMotion) {
        this.track.style.transition = 'none';
      } else {
        this.track.style.transition = '';
      }

      const step = (this.slideWidth || this.viewport.clientWidth) + this.gap;
      const offset = next * step;
      this.track.style.transform = `translate3d(-${offset}px, 0, 0)`;
      this.#syncControls();
      this.#announce();
    }

    #renderDots() {
      if (!this.dotsWrap) return;
      this.dotsWrap.innerHTML = '';
      this.dotsWrap.setAttribute('aria-live', 'polite');
      const pages = this.maxIndex + 1;
      for (let i = 0; i < pages; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'balao-carousel__dot';
        dot.setAttribute('aria-label', `Ir para slide ${i + 1}`);
        dot.addEventListener('click', () => {
          this.#goTo(i);
          this.#restartAutoplay();
        });
        this.dotsWrap.appendChild(dot);
      }
    }

    #syncControls() {
      if (this.prevBtn) this.prevBtn.disabled = !this.loop && this.index <= 0;
      if (this.nextBtn) this.nextBtn.disabled = !this.loop && this.index >= this.maxIndex;

      if (this.dotsWrap) {
        const pages = this.maxIndex + 1;
        if (this.dotsWrap.children.length !== pages) this.#renderDots();
        [...this.dotsWrap.children].forEach((dot, i) => {
          const active = i === this.index;
          dot.setAttribute('aria-current', active ? 'true' : 'false');
          dot.classList.toggle('is-active', active);
          if (active && this.autoplayMs > 0 && !reduceMotion) {
            dot.style.setProperty('--balao-autoplay-ms', `${this.autoplayMs}ms`);
          }
        });
      }
    }

    #announce() {
      if (!this.dotsWrap) return;
      this.dotsWrap.setAttribute(
        'aria-label',
        `Slide ${this.index + 1} de ${this.maxIndex + 1}`
      );
    }
  }

  function initReveal() {
    const items = document.querySelectorAll('[data-balao-reveal]');
    if (!items.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );

    items.forEach((el) => observer.observe(el));
  }

  function openCartDrawer() {
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    const cartLink = document.querySelector('a[href="/cart"], a[href$="/cart"]');
    if (cartLink) cartLink.click();
  }

  async function quickAdd(button) {
    const variantId = button.dataset.variantId;
    const productUrl = button.dataset.productUrl;

    if (!variantId) {
      if (productUrl) window.location.href = productUrl;
      return;
    }

    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    const original = button.innerHTML;

    try {
      const routes = window.Theme?.routes || {};
      const addUrl = routes.cart_add_url || '/cart/add.js';
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
        }),
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Erro ao adicionar');
      }

      button.classList.remove('is-loading');
      button.classList.add('is-success');
      button.innerHTML =
        '<svg class="balao-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5.5 12.25 10 16.5 18.5 7.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      button.setAttribute('aria-label', 'Adicionado ao carrinho');

      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: data }));

      openCartDrawer();

      window.setTimeout(() => {
        button.classList.remove('is-success');
        button.innerHTML = original;
        button.removeAttribute('aria-busy');
      }, 1500);
    } catch (error) {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
      button.innerHTML = original;
      if (productUrl) {
        window.location.href = productUrl;
      } else {
        console.error(error);
      }
    }
  }

  let quickAddBound = false;

  function initQuickAdd() {
    if (quickAddBound) return;
    quickAddBound = true;
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-balao-quick-add]');
      if (!button) return;
      event.preventDefault();
      quickAdd(button);
    });
  }

  const carouselInstances = new WeakMap();
  window.BalaoCarouselInstances = carouselInstances;

  function boot() {
    document.querySelectorAll('[data-balao-carousel]').forEach((root) => {
      if (root.dataset.balaoReady) return;
      root.dataset.balaoReady = 'true';
      const instance = new BalaoCarousel(root);
      carouselInstances.set(root, instance);
    });
    initReveal();
    initQuickAdd();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
